import React, { useEffect, useState } from "react";
import {
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { Feather, Ionicons } from "@expo/vector-icons";

import { extractHashtags, removeHashtags } from "@/formatting/text";
import { type AttributeValue } from "@/schemas/attribute/AttributeSet";
import { type AppUserSession } from "@/schemas/auth/session";
import { UserAttributeType } from "@/schemas/profile/UserAttributeSet";
import ProfileService from "@/services/user/profile";
import { getRandomColor } from "@/utils/colors";

type AboutMeProps = {
  session: AppUserSession;
  aboutMeValue: string;
};

// This could be randomized for new users.
const DEFAULT_BIO =
  "Hello! I'm new here. #NewUser #ExcitedToConnect #OpenToChat";

function AboutMe({ session, aboutMeValue }: AboutMeProps): React.JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [editedBio, setEditedBio] = useState(aboutMeValue || DEFAULT_BIO);
  const [aboutMe, setAboutMe] = useState<AttributeValue[]>([
    {
      id: UserAttributeType.ABOUT_ME,
      label: aboutMeValue || DEFAULT_BIO,
      iconLabel: "",
    },
  ]);

  useEffect(() => {
    if (!aboutMeValue) {
      updateAboutMe(DEFAULT_BIO);
    }
  }, []);

  const cleanBio = removeHashtags(editedBio);
  const tags = extractHashtags(editedBio);

  const updateAboutMe = async (newTextInput: string): Promise<void> => {
    if (newTextInput === aboutMe[0]?.label) {
      return;
    }
    const profileService = new ProfileService(session);
    await profileService.updateAttributes(UserAttributeType.ABOUT_ME, [
      newTextInput,
    ]);
    setAboutMe([
      {
        id: UserAttributeType.ABOUT_ME,
        label: newTextInput,
        iconLabel: "",
      },
    ]);
    setEditedBio(newTextInput);
  };

  const handleEditToggle = (): void => {
    if (isEditing) {
      updateAboutMe(editedBio);
    }
    setIsEditing(!isEditing);
  };

  return (
    <TouchableOpacity onPress={handleEditToggle} style={styles.container}>
      <View style={styles.sectionContainer}>
        <Ionicons name="text-sharp" size={25} color="#01579b" />
        <Text style={styles.sectionTitle}>{"About Me"}</Text>
      </View>
      <View style={styles.contentContainer}>
        <View style={styles.bioContainer}>
          {isEditing ? (
            <TextInput
              style={styles.bioInput}
              value={editedBio}
              onChangeText={setEditedBio}
              multiline
            />
          ) : (
            <Text style={styles.bioText}>{cleanBio}</Text>
          )}
        </View>
        <View style={styles.tagsContainer}>
          {tags.map((tag, index) => (
            <View
              key={index}
              style={[styles.tag, { backgroundColor: getRandomColor() }]}
            >
              <Text style={styles.tagText}>#{tag}</Text>
            </View>
          ))}
        </View>
        <View style={styles.editButton}>
          <Feather
            name={isEditing ? "check" : "edit"}
            size={14}
            color="#007AFF"
          />
        </View>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  sectionContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 20,
    marginLeft: 5,
    fontWeight: "bold",
  },
  bioContainer: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 10,
  },
  bioText: {
    color: "#2D3748",
    flex: 1,
    fontSize: 16,
    marginLeft: 10,
    marginRight: 10,
    textAlign: "left",
  },
  bioInput: {
    color: "#2D3748",
    flex: 1,
    fontSize: 16,
    marginLeft: 10,
    marginRight: 10,
    textAlign: "left",
    borderBottomWidth: 1,
    borderBottomColor: "#CBD5E0",
    paddingBottom: 5,
  },
  contentContainer: {
    padding: 12,
    backgroundColor: "#2C3E50#2C3E50",
  },
  editButton: {
    position: "absolute",
    top: 0,
    right: 0,
  },
  tag: {
    borderRadius: 12,
    marginBottom: 8,
    marginRight: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  tagText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "600",
  },
  tagsContainer: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    marginTop: 10,
  },
});

export default AboutMe;
